import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, withKeys as _withKeys, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-50a711c7"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["aria-expanded"]
const _hoisted_2 = { class: "page-section-navigator__list" }
const _hoisted_3 = ["aria-current", "title", "tabindex", "onClick"]
const _hoisted_4 = { class: "page-section-navigator__label" }
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "page-section-navigator__leader",
  "aria-hidden": "true"
}, null, -1))
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "page-section-navigator__dot",
  "aria-hidden": "true"
}, null, -1))
const _hoisted_7 = ["tabindex", "aria-label", "aria-hidden", "onKeydown"]

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import type { PageSectionNavApi } from './usePageSectionNav';

const INITIAL_SHOW_MS = 2200;
const AUTO_HIDE_MS = 1600;

/**
 * Floating right-side section navigator for multi-section Detail pages.
 * Auto-hides after idle; restores on hover. Collapsed mode shows a peaking
 * dots strip with an attention animation.
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'PageSectionNavigator',
  props: {
    nav: {}
  },
  setup(__props: any) {

const props = __props;

const sections = computed(() => props.nav.sections.value);
const activeId = computed(() => props.nav.activeId.value);
const isVisible = computed(() => props.nav.isVisible.value);

const isCollapsed = ref(false);
const isHovering = ref(false);

let navEl: HTMLElement | null = null;
let collapseTimer: ReturnType<typeof setTimeout> | null = null;

const clearCollapseTimer = () => {
  if (collapseTimer) {
    clearTimeout(collapseTimer);
    collapseTimer = null;
  }
};

const expand = () => {
  isHovering.value = true;
  clearCollapseTimer();
  isCollapsed.value = false;
};

const scheduleCollapse = (delay = AUTO_HIDE_MS) => {
  isHovering.value = false;
  clearCollapseTimer();
  collapseTimer = setTimeout(() => {
    if (!isHovering.value) {
      isCollapsed.value = true;
    }
  }, delay);
};

const onFocusOut = (event: FocusEvent) => {
  const next = event.relatedTarget as Node | null;
  if (navEl && next && navEl.contains(next)) {
    return;
  }
  scheduleCollapse();
};

const onSectionClick = (id: string) => {
  props.nav.scrollTo(id);
  // Keep expanded briefly after click, then auto-hide again.
  expand();
  scheduleCollapse(AUTO_HIDE_MS + 400);
};

const setNavEl = (el: Element | null) => {
  navEl = el as HTMLElement | null;
  if (navEl) {
    syncHeaderOffset();
  }
};

const syncHeaderOffset = () => {
  if (!navEl) {
    return;
  }

  const header = document.getElementById('kt_header');
  const height = header?.getBoundingClientRect().height ?? 88;
  navEl.style.setProperty(
    '--page-section-nav-header-offset',
    `${Math.round(height)}px`,
  );
};

onMounted(() => {
  window.addEventListener('resize', syncHeaderOffset, { passive: true });
  if (isVisible.value) {
    nextTick(() => {
      syncHeaderOffset();
      scheduleCollapse(INITIAL_SHOW_MS);
    });
  }
});

watch(isVisible, (visible) => {
  clearCollapseTimer();
  if (visible) {
    isCollapsed.value = false;
    nextTick(() => {
      syncHeaderOffset();
      scheduleCollapse(INITIAL_SHOW_MS);
    });
  } else {
    isCollapsed.value = false;
    isHovering.value = false;
  }
});

onBeforeUnmount(() => {
  clearCollapseTimer();
  window.removeEventListener('resize', syncHeaderOffset);
});

return (_ctx: any,_cache: any) => {
  return (isVisible.value)
    ? (_openBlock(), _createElementBlock("nav", {
        key: 0,
        ref: setNavEl,
        class: _normalizeClass(["page-section-navigator", {
      'page-section-navigator--collapsed': isCollapsed.value,
      'page-section-navigator--attention': isCollapsed.value,
    }]),
        "aria-label": "Điều hướng mục",
        "aria-expanded": (!isCollapsed.value).toString()
      }, [
        _createElementVNode("div", {
          class: "page-section-navigator__panel",
          onMouseenter: expand,
          onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (scheduleCollapse())),
          onFocusin: expand,
          onFocusout: onFocusOut
        }, [
          _createElementVNode("ul", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section) => {
              return (_openBlock(), _createElementBlock("li", {
                key: section.id,
                class: "page-section-navigator__item"
              }, [
                _createElementVNode("button", {
                  type: "button",
                  class: _normalizeClass(["page-section-navigator__link", {
              'page-section-navigator__link--active': section.id === activeId.value,
            }]),
                  "aria-current": section.id === activeId.value ? 'true' : undefined,
                  title: section.label,
                  tabindex: isCollapsed.value ? -1 : 0,
                  onClick: ($event: any) => (onSectionClick(section.id))
                }, [
                  _createElementVNode("span", _hoisted_4, _toDisplayString(section.label), 1),
                  _hoisted_5,
                  _hoisted_6
                ], 10, _hoisted_3)
              ]))
            }), 128))
          ])
        ], 32),
        _createElementVNode("div", {
          class: "page-section-navigator__peek",
          role: "button",
          tabindex: isCollapsed.value ? 0 : -1,
          "aria-label": isCollapsed.value ? 'Hiện điều hướng mục' : undefined,
          "aria-hidden": (!isCollapsed.value).toString(),
          onMouseenter: expand,
          onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (scheduleCollapse())),
          onFocusin: expand,
          onClick: expand,
          onKeydown: [
            _withKeys(_withModifiers(expand, ["prevent"]), ["enter"]),
            _withKeys(_withModifiers(expand, ["prevent"]), ["space"])
          ]
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section) => {
            return (_openBlock(), _createElementBlock("span", {
              key: `peek-${section.id}`,
              class: _normalizeClass(["page-section-navigator__peek-dot", {
          'page-section-navigator__peek-dot--active': section.id === activeId.value,
        }])
            }, null, 2))
          }), 128))
        ], 40, _hoisted_7)
      ], 10, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})